/**
 * taskflow-wbs-engine.ts
 *
 * Work Breakdown Structure (WBS) Tree Builder and Roll-Up Aggregation Engine.
 * Synthesizes polymorphic hierarchies, rolls up completion percentages
 * (excluding cancelled/archived nodes), and normalizes work estimates.
 */

import type { TaskFlowItem } from '../contracts/taskflow-schema.js';

export interface WbsNode {
  item: TaskFlowItem;
  children: WbsNode[];
  level: number;
  rollupProgress: number;
  rollupEstimateHours: number;
  rollupActualHours: number;
  totalDescendants: number;
}

export class WbsEngine {
  /**
   * Builds full hierarchical WBS tree from a flat list of items
   */
  static buildTree(items: TaskFlowItem[]): WbsNode[] {
    const itemMap = new Map<string, TaskFlowItem>();
    const childrenMap = new Map<string, TaskFlowItem[]>();

    for (const item of items) {
      itemMap.set(item.id, item);
    }

    const rootItems: TaskFlowItem[] = [];

    for (const item of items) {
      if (!item.parent || !itemMap.has(item.parent)) {
        rootItems.push(item);
      } else {
        const list = childrenMap.get(item.parent) || [];
        list.push(item);
        childrenMap.set(item.parent, list);
      }
    }

    return rootItems.map((root) => this.buildNode(root, childrenMap, 0));
  }

  private static buildNode(
    item: TaskFlowItem,
    childrenMap: Map<string, TaskFlowItem[]>,
    level: number
  ): WbsNode {
    const rawChildren = childrenMap.get(item.id) || [];
    const children = rawChildren.map((c) => this.buildNode(c, childrenMap, level + 1));

    let rollupProgress = item.progressPercentage ?? 0;
    let rollupEstimateHours = item.estimateHours ?? 0;
    let rollupActualHours = item.actualHours ?? 0;
    let totalDescendants = children.length;

    for (const child of children) {
      totalDescendants += child.totalDescendants;
      rollupEstimateHours += child.rollupEstimateHours;
      rollupActualHours += child.rollupActualHours;
    }

    // Compute progress roll-up if item has children
    if (children.length > 0) {
      // Filter out cancelled and archived tasks from denominator
      const activeChildren = children.filter(
        (c) => c.item.status !== 'cancelled' && c.item.status !== 'archived'
      );

      if (activeChildren.length > 0) {
        const sum = activeChildren.reduce((acc, c) => acc + c.rollupProgress, 0);
        rollupProgress = Math.round(sum / activeChildren.length);
      } else {
        // All children are cancelled/archived
        rollupProgress = item.status === 'done' ? 100 : 0;
      }
    }

    return {
      item,
      children,
      level,
      rollupProgress: isNaN(rollupProgress) ? 0 : Math.min(100, Math.max(0, rollupProgress)),
      rollupEstimateHours: isNaN(rollupEstimateHours) ? 0 : rollupEstimateHours,
      rollupActualHours: isNaN(rollupActualHours) ? 0 : rollupActualHours,
      totalDescendants,
    };
  }

  /**
   * Flatten tree to list with depth indicators for virtualized rendering
   */
  static flattenTree(
    nodes: WbsNode[],
    collapsedSet: Set<string> = new Set()
  ): Array<{ node: WbsNode; isCollapsed: boolean; hasChildren: boolean }> {
    const result: Array<{ node: WbsNode; isCollapsed: boolean; hasChildren: boolean }> = [];

    function traverse(nodeList: WbsNode[]) {
      for (const node of nodeList) {
        const hasChildren = node.children.length > 0;
        const isCollapsed = collapsedSet.has(node.item.id);

        result.push({ node, isCollapsed, hasChildren });

        if (hasChildren && !isCollapsed) {
          traverse(node.children);
        }
      }
    }

    traverse(nodes);
    return result;
  }
}
